import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Button,
  DrawerBody,
  DrawerFooter,
  FormStatusMessage,
  Link,
  LoadingButton,
  TextField,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useLazyDataModel } from '@thd-nucleus/data-sources';
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';

import * as yup from 'yup';
// ****
import { useDrawer } from '../../../../../hooks/use-drawer';
import { useInstantCheckoutStatus } from '../../../../../hooks/use-instant-checkout-status';
import { usePayments } from '../../../../../hooks/use-payments';
// ****
import { track } from '../../../../../analytics/analytics';
import {
  INSTANT_CHECKOUT_BUTTON_SUBMISSION,
  INSTANT_CHECKOUT_SUCCESS,
  POST_PAGE_ERROR,
} from '../../../../../constants/analytics';
import {
  ERR_GENERIC,
  ERR_INVALID_PAYMENT,
  ERR_PAYMENT_ALREADY_ADDED,
  ERR_RECAPTCHA,
  ERR_REDIRECT,
} from '../../../../../constants/response-error-messages';
import {
  INSTANT_CHECKOUT_ENROLLMENT_SUCCESS,
  INSTANT_CHECKOUT_PAYMENT_UPDATE_SUCCESS,
} from '../../../../../constants/response-success-messages';
import { useAddress } from '../../../../../hooks/use-address';
import { useViews } from '../../../../../hooks/use-views';
import { TAlertStatus, TAlertType } from '../../../../../models/common/alert';
import {
  b2cProfile,
  updateInstantCheckoutSettingsAdd,
  orangePayToken,
  addAddresses,
} from '../../../../../models/graphQLModels';
import {
  CHANGE_HOW_YOU_PAY,
  CONFIRM_LEAVE,
  SETUP_HOW_YOU_PAY,
} from '../../../../../models/views';
import { getTextFieldStatus } from '../../../../../utils/forms';
import {
  getChannelID,
  getCustomerIp,
  redirectToSignIn,
} from '../../../../../utils/general';

import { SelectAddressView } from '../../Addresses/SelectAddressView/SelectAddressView';
import {
  OrangePayCardBrandFilterValue,
  TOrangePaySuccessResponse,
} from '../../../../../utils/orangePay';
import { icAddPaymentRule } from '../../../../../utils/validation-rules/instant-checkout-rule';
import { SkeletonLoader } from '../../../../shared/SkeletonLoader';
import { AddressDropdownSelector } from '../../../../shared/address-dropdown';
import { InstantCheckoutDrawerHeader } from '../../../InstantCheckoutDrawerHeader';
import { IAddress } from '../../../../../models/address';
import { IPaymentInput } from '../../../../../models/payments';
import { AddAddressView } from '../../Addresses/InstantCheckoutBillingDelivery/AddAddressView';
import {
  getIsAuthError,
  getIsCardAlreadyOnAccount,
  isInvalidPaymentAuthAttempt,
} from '../../../../../constants/error-codes';
import { shouldShowCardExpirationWithBrand } from '../../../../../utils/payments';
import {
  createNewRelicActions,
  logNewRelicAction,
} from '../../../../../utils/newrelic';
import { getCookie } from '../../../../../utils/cookies';
import { getSiteKey } from '../../../../../utils/credit-cards';

const OrangePayFieldsDynamicImport = lazy(
  () => import('../../../../shared/OrangePayContent'),
);
const validationSchema = yup.object().shape(icAddPaymentRule);

export type TICPaymentFormValuesAddCard = {
  cardNickName?: string;
  cardHolderName: string;
  billingAddressID: number | null | undefined;
  recaptcha: string;
};

export const InstantCheckoutAddPayment = () => {
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isOrangePayLoaded, setIsOrangePayLoaded] = useState(false);
  const [showRecaptchaError, setShowRecaptchaError] = useState(false);
  const disabledFormClasses = 'sui-pointer-events-none sui-opacity-30';
  const ReCAPTCHARef = useRef<ReCAPTCHA>(null);
  const [isMicroUIFormValid, setIsMicroUIFormValid] = useState(false);
  const [isMicroUILoading, setIsMicroUILoading] = useState(true);
  const [sendOrangePayRemoteAuth, setSendOrangePayRemoteAuth] = useState(false);
  const [loadAddBilling, setLoadAddBilling] = useState(false);
  const [loadSelectBilling, setLoadSelectBilling] = useState(false);

  const [orangePaySuccessData, setOrangePaySuccessData] =
    useState<TOrangePaySuccessResponse | null>(null);

  const { goToView, flowName, currentView } = useViews();
  const { refetchPayments, hasPayments } = usePayments();
  const { hideDrawer } = useDrawer();
  const { refetchAddresses, selectedAddress, addresses, defaultAddress } =
    useAddress();
  const actions = createNewRelicActions(currentView);

  const assignErrorMessage = (message: string | null) => {
    setShowRecaptchaError(false);
    setErrorMsg(message);
    if (message) {
      resetReCaptcha();
    }
  };

  const {
    refetchProfile,
    setAlertMessage,
    profile,
    userId,
    customerAccountId,
  } = useInstantCheckoutStatus();

  const formMethods = useForm<TICPaymentFormValuesAddCard>({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const handleAddAPIError = (errors: []) => {
    if (getIsCardAlreadyOnAccount(errors)) {
      assignErrorMessage(ERR_PAYMENT_ALREADY_ADDED.message);
    } else if (isInvalidPaymentAuthAttempt(errors)) {
      assignErrorMessage(ERR_INVALID_PAYMENT.message);
    } else {
      assignErrorMessage(ERR_GENERIC.message);
    }
  };

  const {
    control,
    formState: { errors: formErrors, isValid },
    handleSubmit,
    resetField,
    getValues,
  } = formMethods;

  const [callUpdateInstantCheckout, updateInstantCheckoutData] =
    useLazyDataModel('updateInstantCheckoutSettings');

  const {
    called: updateInstantCheckoutCalled,
    loading: updateInstantCheckoutLoading,
    error: updateInstantCheckoutError,
    data: updateInstantCheckoutResult,
  } = updateInstantCheckoutData;

  useEffect(() => {
    if (updateInstantCheckoutError) {
      logNewRelicAction(actions.ADD_PAYMENT, updateInstantCheckoutError);
      if (getIsAuthError(updateInstantCheckoutError)) {
        setAlertMessage({
          alertType: TAlertType.GENERAL,
          alertStatus: TAlertStatus.INFO,
          text: ERR_REDIRECT.message,
        });
        redirectToSignIn();
      }
      if (
        updateInstantCheckoutError?.graphQLErrors &&
        Array.isArray(updateInstantCheckoutError.graphQLErrors)
      ) {
        handleAddAPIError(updateInstantCheckoutError.graphQLErrors);
      } else {
        assignErrorMessage(ERR_GENERIC.message);
      }

      setLoading(false);
      track(POST_PAGE_ERROR, {
        pageSection: 'add payment overlay',
        message: ERR_GENERIC.message,
        code: updateInstantCheckoutError?.errorCode || 'n/a',
        type: 'error',
        displayType: 'inline',
      });
    }

    if (
      updateInstantCheckoutResult &&
      updateInstantCheckoutCalled &&
      !updateInstantCheckoutLoading &&
      !updateInstantCheckoutError
    ) {
      setLoading(false);
      logNewRelicAction(actions.ADD_PAYMENT);
      track(INSTANT_CHECKOUT_SUCCESS, {
        action: 'add',
        item: 'new payment method',
      });
      if (flowName === CHANGE_HOW_YOU_PAY || flowName === SETUP_HOW_YOU_PAY) {
        setAlertMessage({
          alertType: TAlertType.IC_PAYMENT_UPDATE,
          alertStatus: TAlertStatus.SUCCESS,
          text: INSTANT_CHECKOUT_PAYMENT_UPDATE_SUCCESS.message,
        });
      } else {
        setAlertMessage({
          alertType: TAlertType.TOP,
          alertStatus: TAlertStatus.SUCCESS,
          text: INSTANT_CHECKOUT_ENROLLMENT_SUCCESS.message,
        });
      }
      refetchAddresses();
      refetchPayments();
      refetchProfile();
      hideDrawer();
    }
  }, [
    updateInstantCheckoutError,
    updateInstantCheckoutCalled,
    updateInstantCheckoutLoading,
    updateInstantCheckoutError,
  ]);

  const handleClose = () => {
    track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
      overlayType: 'add new payment',
      action: 'cancel',
    });
    if (flowName === CHANGE_HOW_YOU_PAY || flowName === SETUP_HOW_YOU_PAY) {
      hideDrawer();
    } else {
      goToView(CONFIRM_LEAVE);
    }
  };

  const customGoBack = () => {
    // if both add and select are true, then the user clicked on the add button on the select Billing Address View
    if (loadAddBilling && loadSelectBilling) {
      setLoadAddBilling(false);
    } else {
      setLoadAddBilling(false);
      setLoadSelectBilling(false);
    }
  };

  const onSubmitError = () => {
    // if orange pay fails to load do nothing
    if (!isOrangePayLoaded) {
      return;
    }

    if (!(isValid === false && isMicroUIFormValid === true)) {
      setSendOrangePayRemoteAuth(true);
    }
  };

  const viewAllClick = () => {
    setLoadSelectBilling(true);
    // goToView(SELECT_BILLING_ADDRESS);
  };

  const addBillingClick = () => {
    setLoadAddBilling(true);
  };

  const resetReCaptcha = () => {
    resetField('recaptcha');
    ReCAPTCHARef.current?.reset();
  };

  const onReCaptchaError = () => {
    setShowRecaptchaError(true);
  };

  const validateOrangePayFields = () => {
    if (!isOrangePayLoaded) {
      assignErrorMessage(ERR_GENERIC.message);
      return;
    }
    if (!isValid) {
      return;
    }
    track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
      overlayType: 'add new payment',
      action: 'finish enrollment',
    });
    setLoading(true);
    setSendOrangePayRemoteAuth(true);
  };

  useEffect(() => {
    if (errorMsg) {
      resetReCaptcha();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMsg]);

  useEffect(() => {
    if (orangePaySuccessData) {
      const { billingAddressID, cardHolderName, cardNickName, recaptcha } =
        getValues();
      const billingAddress = addresses.find(
        (address: IAddress) => address.id === billingAddressID,
      );

      if (!billingAddress) {
        assignErrorMessage(ERR_GENERIC.message);
        return;
      }
      const cardBrand =
        OrangePayCardBrandFilterValue[
          orangePaySuccessData.cardDetails.reportPaymentMethodCode
        ].toUpperCase();

      const shouldShowExpiration = shouldShowCardExpirationWithBrand(cardBrand);

      const payment: IPaymentInput = {
        billingAddress,
        isDefault: !hasPayments(),
        isInstantCheckoutDefault: true,
        buyerId: orangePaySuccessData.cardDetails.authorizedBuyerID || null,
        customerIP: getCustomerIp(),
        forterToken: getCookie('forterToken') || '',
        type: 'CREDIT_CARD',
        nickName: cardNickName || null,
        xrefCardNumber: orangePaySuccessData.cardDetails.accountNumberXref,
        cardDetails: {
          ...orangePaySuccessData.cardDetails,
          cardHolderName,
          expirationMonth: orangePaySuccessData.cardDetails.expirationMonth,
          expirationYear: orangePaySuccessData.cardDetails.expirationYear,
          cardBrand: {
            name: cardBrand,
            paymentMethodCode: orangePaySuccessData.paymentMethodCode,
            reportPaymentMethodCode:
              orangePaySuccessData.reportPaymentMethodCode,
          },
        },
        cardReference: {
          ...orangePaySuccessData.cardReference,
        },
      };

      // we don't send expiration details with cards that don't have expiration dates (HD Consumer, HD Commerical, PROX)

      if (!shouldShowExpiration) {
        delete payment.cardDetails.expirationMonth;
        delete payment.cardDetails.expirationYear;
      }

      delete payment.cardDetails.authorizedBuyerID;
      delete payment.cardDetails.reportPaymentMethodCode;
      delete payment.cardDetails.paymentMethodCode;

      callUpdateInstantCheckout({
        variables: {
          userId,
          customerAccountId,
          channelId: getChannelID(),
          address: selectedAddress || defaultAddress,
          paymentCard: payment,
          account: profile,
        },
        ssr: false,
        context: {
          withAuth: true,
          headers: {
            'X-PAA-AUTH': recaptcha,
          },
        },
      });
    }
  }, [orangePaySuccessData]);

  const successHandler = (cardData: TOrangePaySuccessResponse) => {
    setOrangePaySuccessData(cardData);
  };

  return (
    <>
      {isMicroUILoading ? <SkeletonLoader /> : null}
      <div hidden={isMicroUILoading} data-testid="add-payment-section">
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(validateOrangePayFields, onSubmitError)}>
            {loadAddBilling || loadSelectBilling ? (
              <>
                {loadSelectBilling && !loadAddBilling && (
                  <SelectAddressView
                    addressType="Billing"
                    setLoadSelectBilling={setLoadSelectBilling}
                    setLoadAddBilling={setLoadAddBilling}
                    customGoBack={customGoBack}
                  />
                )}
                {loadAddBilling && (
                  <AddAddressView
                    setLoadAddBilling={setLoadAddBilling}
                    addressType="billing"
                    customGoBack={customGoBack}
                    setLoadSelectBilling={setLoadSelectBilling}
                  />
                )}
              </>
            ) : (
              <>
                <InstantCheckoutDrawerHeader
                  title="Add New Payment"
                  onClose={handleClose}
                />
                <DrawerBody data-testid="add-payment-drawer">
                  <>
                    {showRecaptchaError && (
                      <div className="sui-mb-6">
                        <Alert status="error">{ERR_RECAPTCHA.message}</Alert>
                      </div>
                    )}
                    <div
                      className={`sui-grid sui-gap-y-6 sui-mb-10 ${
                        showRecaptchaError ? disabledFormClasses : ''
                      }`}
                    >
                      <Controller
                        name="cardNickName"
                        control={control}
                        disabled={showRecaptchaError}
                        render={({ field }) => (
                          <TextField
                            label="Card Nickname"
                            status={getTextFieldStatus(formErrors.cardNickName)}
                            statusMessage={formErrors.cardNickName?.message}
                            InputProps={{
                              ...field,
                              inputProps: {
                                'data-testid': 'cardNickNameField',
                              },
                            }}
                            optional
                          />
                        )}
                      />
                      <Controller
                        name="cardHolderName"
                        control={control}
                        disabled={showRecaptchaError}
                        render={({ field }) => (
                          <TextField
                            label="Name on Card"
                            status={getTextFieldStatus(
                              formErrors.cardHolderName,
                            )}
                            statusMessage={formErrors.cardHolderName?.message}
                            InputProps={{
                              ...field,
                              inputProps: {
                                'data-qm-mask': '',
                                'data-testid': 'cardHolderNameField',
                              },
                            }}
                            required
                          />
                        )}
                      />
                      {typeof window !== 'undefined' ? (
                        <Suspense fallback={<></>}>
                          <OrangePayFieldsDynamicImport
                            isCVVOnly={false}
                            isOrangePayLoaded={isOrangePayLoaded}
                            assignErrorMessage={assignErrorMessage}
                            setLoading={setLoading}
                            setIsOrangePayLoaded={setIsOrangePayLoaded}
                            setIsMicroUIFormValid={setIsMicroUIFormValid}
                            setIsMicroUILoading={setIsMicroUILoading}
                            isMicroUILoading={isMicroUILoading}
                            sendOrangePayRemoteAuth={sendOrangePayRemoteAuth}
                            setSendOrangePayRemoteAuth={
                              setSendOrangePayRemoteAuth
                            }
                            successHandler={successHandler}
                            view={currentView}
                          />
                        </Suspense>
                      ) : (
                        <></>
                      )}
                      <Controller
                        name="billingAddressID"
                        control={control}
                        render={({ field }) => {
                          const {
                            value: fieldValue,
                            onChange,
                            ref,
                            ...rest
                          } = field;
                          return (
                            <AddressDropdownSelector
                              selectedAddrID={fieldValue}
                              statusType={getTextFieldStatus(
                                formErrors.billingAddressID,
                              )}
                              statusMessage={
                                formErrors.billingAddressID?.message
                              }
                              onChange={onChange}
                              {...rest}
                            />
                          );
                        }}
                      />
                      <div className="sui-grid sui-grid-cols-2 sui-gap-32 sm:sui-gap-x-32">
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <Link
                          align="left"
                          component="button"
                          underline="always"
                          data-testid="view-all-link"
                          onClick={viewAllClick}
                        >
                          View All
                        </Link>
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <Link
                          align="right"
                          component="button"
                          underline="always"
                          data-testid="add-payment-link"
                          onClick={addBillingClick}
                        >
                          + Add Address
                        </Link>
                      </div>
                      <div className="sui-mt-10">
                        <Controller
                          name="recaptcha"
                          control={control}
                          disabled={showRecaptchaError}
                          render={({ field: { onChange } }) => (
                            <ReCAPTCHA
                              ref={ReCAPTCHARef}
                              sitekey={getSiteKey()}
                              size={isMobileOnly ? 'compact' : 'normal'}
                              onChange={onChange}
                              onErrored={onReCaptchaError}
                              data-testid="addPaymentRecaptcha"
                            />
                          )}
                        />
                        <FormStatusMessage status="error">
                          {formErrors.recaptcha?.message}
                        </FormStatusMessage>
                      </div>
                      {errorMsg &&
                        (errorMsg === ERR_INVALID_PAYMENT.message ? (
                          <Alert status="error">
                            <Typography>
                              Something went wrong. Correct one or more of these
                              possible issues:
                            </Typography>
                            <Typography component="div">
                              <ul className="sui-mt-4 sui-list-disc sui-list-outside sui-pl-7 ">
                                <li>Card details are not accurate</li>
                                <li>Card was declined by bank/card user</li>
                              </ul>
                            </Typography>
                          </Alert>
                        ) : (
                          <div className="sui-mt-10">
                            <Alert status="error">{errorMsg}</Alert>
                          </div>
                        ))}
                    </div>
                  </>
                </DrawerBody>
                <DrawerFooter>
                  <div className="sui-flex sui-gap-2">
                    <Button fullWidth variant="secondary" onClick={handleClose}>
                      Cancel
                    </Button>
                    <LoadingButton
                      loading={loading}
                      variant="primary"
                      type="submit"
                      data-testid="finishEnrollmentBtn"
                      fullWidth
                    >
                      Finish Enrollment
                    </LoadingButton>
                  </div>
                </DrawerFooter>
              </>
            )}
          </form>
        </FormProvider>
      </div>
    </>
  );
};

InstantCheckoutAddPayment.dataModel = {
  b2cProfile,
  updateInstantCheckoutSettings: updateInstantCheckoutSettingsAdd,
  orangePayToken,
  addAddresses,
};
